<template>
  <div class="inline-flex">
    <!-- prevent default is necessary because otherwise these buttons will force submit forms  -->
    <button
      ref="minusButton"
      class="counter"
      :disabled="disableMinus"
      cursor="disabled"
      :aria-label="`Decrease ${label} by ${factor}`"
      aria-describedby="counter-label"
      @click.prevent="handleDecrease"
    >
      <subtract-outline-icon :color="minusColor" :class="disableMinusCursor" :width="40" :height="40" />
    </button>

    <h3 id="counter-label" class="select-none px-12 my-auto" aria-live="assertive">
      <!-- @slot Default slot for counter content. Do not name this slot, it will break Rate. -->
      <slot :count="count" :label="label">
        <span class="font-medium text-m2"> {{ count }} </span>
        <span class="text-grey-text-subdued"> {{ label }} </span>
      </slot>
    </h3>
    <button
      ref="addButton"
      class="counter"
      :disabled="disableAdd"
      :aria-label="`Increase ${label} by ${factor}`"
      aria-describedby="counter-label"
      @click.prevent="handleIncrease"
    >
      <add-outline-icon :color="addColor" :class="disableAddCursor" :width="40" :height="40" />
    </button>
  </div>
</template>

<script lang="ts">
import AddOutlineIcon from '../../graphics/icons/AddOutlineIcon.vue';
import SubtractOutlineIcon from '../../graphics/icons/SubtractOutlineIcon.vue';

/**
 * Counter is currently used to visually represent duration or rate of pay.
 * For duration, Counter is used as a standalone component and can be found here: https://www.sittercity.com/adventures/new/duration.
 * For rate, Counter is imported as a child component and has its slot overridden.
 */
export default {
  name: 'Counter',
  components: {
    AddOutlineIcon,
    SubtractOutlineIcon,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    /**
     * Hour or dollar.
     */
    factor: {
      type: Number,
      default: 1,
    },
    /**
     * Text shown next to the counter.
     * Label is important for accessibility.
     * Please check the screenreader.
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * Minimum is used to determine when the Minus button is disabled.
     */
    min: {
      type: Number,
      default: 1,
    },
    /**
     * Maximum is used to determine when the Add button is disabled.
     */
    max: {
      type: Number,
      default: 12,
    },
    /**
     * Value prop sets the initial value of the `count` property on data.
     * Count is what gets manipulated and passed to Rate.
     */
    modelValue: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      count: this.modelValue,
    };
  },
  computed: {
    disableAdd(): Boolean {
      return this.count >= this.max;
    },
    disableMinus(): Boolean {
      return this.count <= this.min;
    },
    /**
     * Computed properties below have to be calculated for each button
     * so they don't get disabled at the same time
     */
    disableAddCursor(): String {
      return this.disableAdd ? 'cursor-not-allowed' : 'cursor-pointer';
    },
    disableMinusCursor(): String {
      return this.disableMinus ? 'cursor-not-allowed' : 'cursor-pointer';
    },
    minusColor(): String {
      return this.disableMinus ? '#8A8A8F' : '#99005c';
    },
    addColor(): String {
      return this.disableAdd ? '#8A8A8F' : '#99005c';
    },
  },
  watch: {
    /**
     * Instead of emitting input on both handleDecrease and handleIncrease,
     * watcher emits input on any change to the count
     */
    count(newValue) {
      this.$emit('update:modelValue', newValue);
    },
    /**
     * If modelValue prop changes, reload the counter
     */
    modelValue(parentNum) {
      this.count = parentNum;
    },
  },
  methods: {
    handleDecrease(): void {
      if (this.count - this.factor <= this.min) {
        /**
         * This disables the minus button
         */
        this.count = this.min;
        /**
         * After minus button is disabled, user will not be able to continue using the keyboard,
         * unless we manually shift focus to the add button.
         * Casting it as HTMLButtonElement makes Typescript happy.
         */
        (this.$refs.addButton as HTMLButtonElement).focus();

        return;
      }
      this.count -= this.factor;
    },
    handleIncrease(): void {
      if (this.count + this.factor >= this.max) {
        this.count = this.max;
        (this.$refs.minusButton as HTMLButtonElement).focus();

        return;
      }
      this.count += this.factor;
    },
  },
};
</script>

<style lang="scss" scoped>
.counter {
  &:focus-visible {
    border-radius: 50%;
    outline: 2px dotted theme('colors.primary.DEFAULT');
    outline-offset: 2px;
  }
}
</style>
